'use client';

import { useCallback } from 'react';
import { getUserAccountPublicKeySync } from '@velocity-exchange/sdk';
import invariant from 'tiny-invariant';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';
import useDriftClient from 'src/hooks/useDriftClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { updateUserNameIx } from '@velocity-exchange/common';

/**
 * Hook for renaming a subaccount.
 */
export const useRenameSubaccount = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const renameSubaccountAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, accountId: number, newName: string) => {
			invariant(driftClient, 'driftClient is required');
			const userAccountPublicKey = getUserAccountPublicKeySync(
				driftClient.program.programId,
				driftClient.wallet.publicKey,
				accountId
			);
			const ix = await updateUserNameIx({
				velocityClient: driftClient,
				newName,
				userAccountPublicKey,
				subAccountId: accountId,
			});
			await wallet.signAndSendTransaction(ix, getTxParams());
		},
		[driftClient, getTxParams]
	);

	const wrappedRenameSubaccount = useSponsoredSignDriftActionWithFallback(
		renameSubaccountAction
	);

	const renameSubaccount = useCallback(
		async (accountId: number, newName: string): Promise<boolean> => {
			if (!driftClient) return false;

			const toastId = `renamingSubaccount${accountId}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedRenameSubaccount(accountId, newName);

				notify({
					id: toastId,
					type: 'success',
					message: 'Account name updated.',
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[driftClient, walletAdapter, wrappedRenameSubaccount]
	);

	return { renameSubaccount };
};
